import { useEffect } from 'react';
import apiService, { SEOSettings } from '../services/api';

interface UseSEOProps {
  entityType?: string;
  entityId?: number | string; // Allow both number and string for pages
  fallbackTitle?: string;
  fallbackDescription?: string;
  fallbackImage?: string;
  entityData?: any; // Product or category data for auto-generating keywords
}

export const useSEO = ({ 
  entityType, 
  entityId, 
  fallbackTitle = 'Lillaflora - Mysig blomsterbutik i Kramfors',
  fallbackDescription = 'Lillaflora - En mysig blomsterbutik med inredningsdetaljer i Kramfors, Höga kusten',
  fallbackImage = '/static/images/default-og-image.jpg',
  entityData
}: UseSEOProps = {}) => {
  
  useEffect(() => {
    const updateSEO = async () => {
      let seoData: SEOSettings | null = null;
      
      // Fetch SEO data if entity is specified
      if (entityType && entityId) {
        seoData = await apiService.getSEOForEntity(entityType, entityId);
      }
      
      // Check if server has already provided SEO - don't override if it has
      const existingKeywords = document.querySelector('meta[name="keywords"]')?.getAttribute('content');
      const hasServerSEO = existingKeywords && 
        !existingKeywords.includes('lillaflora, kramfors, blomsterbutik, inredning, ljus, doftljus, mayas cottage, höga kusten');
      
      // Only update SEO if we have specific data or no server SEO exists
      if (seoData || !hasServerSEO) {
        const canonicalHref = buildCanonicalHref(seoData?.canonical_url);

        // Update document title
        const title = seoData?.meta_title || fallbackTitle;
        document.title = title;
        
        // Update meta description
        updateMetaTag('description', seoData?.meta_description || fallbackDescription);
        
        // Update meta keywords - use SEO data or auto-generate
        const keywords = seoData?.meta_keywords || generateAutoKeywords(entityType, entityData);
        if (keywords) {
          updateMetaTag('keywords', keywords);
        }
        
        // Update robots meta
        updateMetaTag('robots', seoData?.robots_meta || 'index,follow');
        
        // Update Open Graph tags
        updateMetaProperty('og:title', seoData?.og_title || title);
        updateMetaProperty('og:description', seoData?.og_description || seoData?.meta_description || fallbackDescription);
        updateMetaProperty('og:image', seoData?.og_image || fallbackImage);
        updateMetaProperty('og:type', 'website');
        updateMetaProperty('og:url', canonicalHref);
        
        // Update canonical URL
        updateLinkRel('canonical', canonicalHref);
      }
    };
    
    updateSEO();
  }, [entityType, entityId, fallbackTitle, fallbackDescription, fallbackImage, entityData]);
};

// Helper to build a clean canonical URL:
// - Prefer explicit canonical_url from SEO settings when present
// - Otherwise use current origin + pathname (no query params or hash)
const buildCanonicalHref = (seoCanonical?: string | null): string => {
  if (seoCanonical && typeof seoCanonical === 'string') {
    return seoCanonical;
  }
  if (typeof window === 'undefined') {
    return '';
  }
  const { origin, pathname } = window.location;
  return `${origin}${pathname}`;
};

// Helper function to update meta tags
const updateMetaTag = (name: string, content: string) => {
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

// Helper function to update Open Graph meta properties
const updateMetaProperty = (property: string, content: string) => {
  let meta = document.querySelector(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('property', property);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

// Helper function to update link relations (like canonical)
const updateLinkRel = (rel: string, href: string) => {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  // For canonical URLs, prefer the server-rendered value (from seo.php) if it exists.
  // This ensures that manually configured canonicals in the admin are not overridden
  // by client-side fallbacks.
  if (rel === 'canonical' && link.getAttribute('href')) {
    return;
  }
  link.setAttribute('href', href);
};

// Helper function to auto-generate keywords based on entity data
const generateAutoKeywords = (entityType?: string, entityData?: any): string => {
  const baseKeywords = ['lillaflora', 'kramfors', 'blomsterbutik', 'inredning', 'ljus', 'doftljus', 'mayas cottage', 'höga kusten'];
  let keywords = [...baseKeywords];
  
  if (entityType === 'product' && entityData) {
    // Add product-specific keywords
    if (entityData.name) {
      // Extract words from product name (remove common words)
      const nameWords = entityData.name
        .toLowerCase()
        .replace(/[^a-zA-ZåäöÅÄÖ\s]/g, '') // Keep only letters and spaces
        .split(/\s+/)
        .filter((word: string) => 
          word.length > 2 && 
          !['för', 'med', 'och', 'till', 'från', 'som', 'den', 'det', 'ett', 'är', 'var', 'har', 'kan', 'ska'].includes(word)
        );
      keywords.unshift(...nameWords);
    }
    
    // Add category keywords
    if (entityData.category) {
      const categoryWords = entityData.category
        .toLowerCase()
        .replace(/[^a-zA-ZåäöÅÄÖ\s]/g, '')
        .split(/\s+/)
        .filter((word: string) => word.length > 2);
      keywords.push(...categoryWords);
    }
    
    // Add description keywords (first few meaningful words)
    if (entityData.description) {
      const descWords = entityData.description
        .toLowerCase()
        .replace(/[^a-zA-ZåäöÅÄÖ\s]/g, '')
        .split(/\s+/)
        .filter((word: string) => 
          word.length > 3 && 
          !['för', 'med', 'och', 'till', 'från', 'som', 'den', 'det', 'ett', 'är', 'var', 'har', 'kan', 'ska', 'denna', 'detta'].includes(word)
        )
        .slice(0, 5); // Take only first 5 meaningful words
      keywords.push(...descWords);
    }
    
    // Add price range keywords
    if (entityData.price) {
      const price = parseFloat(entityData.price);
      if (price < 100) keywords.push('billig', 'prisvärd');
      else if (price < 300) keywords.push('mellan', 'prisvärd');
      else keywords.push('premium', 'exklusiv');
    }
  }
  
  // Remove duplicates and limit to reasonable number
  const uniqueKeywords = Array.from(new Set(keywords))
    .filter((keyword: string) => keyword && keyword.length > 1)
    .slice(0, 15); // Limit to 15 keywords max
  
  return uniqueKeywords.join(', ');
};
